import { Alert, Card, CardContent, CircularProgress, Stack, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import type React from "react";
import { useCallback, useState } from "react";
import { useApi, useApiQuery } from "../../hooks";
import type { TestVariable } from "../../types";
import { EditableVariableList } from "../editable-variable-list";

export const GlobalVariablesTab: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { callApi } = useApi<TestVariable>();
  const [isUpdating, setIsUpdating] = useState(false);

  const {
    data: globalVariables,
    isLoading,
    refetch,
  } = useApiQuery<TestVariable[]>("/global-variables");

  const handleVariablesChange = useCallback(
    async (variables: TestVariable[]) => {
      setIsUpdating(true);
      try {
        // Get current variables to find which ones to delete
        const currentKeys = new Set((globalVariables || []).map((v) => v.key));
        const newKeys = new Set(variables.map((v) => v.key));

        // Delete variables that are no longer present
        for (const key of currentKeys) {
          if (!newKeys.has(key)) {
            await callApi(`/global-variables/${encodeURIComponent(key)}`, {
              method: "DELETE",
            });
          }
        }

        // Create or update each variable
        for (const variable of variables) {
          // Ensure context is set to global
          const varToSave = { ...variable, context: "global" as const };
          await callApi("/global-variables", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ variable: varToSave }),
          });
        }

        enqueueSnackbar("Global variables updated successfully", {
          variant: "success",
        });
        refetch();
      } catch {
        enqueueSnackbar("Failed to update global variables", {
          variant: "error",
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [callApi, enqueueSnackbar, globalVariables, refetch],
  );

  return (
    <Card>
      <CardContent>
        <Stack spacing={3}>
          <Stack spacing={1}>
            <Typography variant="h6">Global Variables</Typography>
            <Typography variant="body2" color="text.secondary">
              Global variables are available to all tests in your organization. They have the
              highest precedence in the variable hierarchy and will override variables defined at
              other levels (app_config, persona, suite, test).
            </Typography>
          </Stack>

          <Alert severity="info">
            Variable precedence (highest to lowest): global &gt; app_config &gt; persona &gt; suite
            &gt; test
          </Alert>

          {isLoading ? (
            <Stack alignItems="center" py={4}>
              <CircularProgress />
            </Stack>
          ) : (
            <EditableVariableList
              variables={globalVariables || []}
              onVariablesChange={handleVariablesChange}
              isLoading={isUpdating}
              hideTitle
            />
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
